import { useCallback, useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { WebsiteForm } from '../components';
import Frame from '../layout/Frame';
import { fetchWebsiteDetails, updateWebsite } from '../requests/requests';
import { WebsiteSettings } from '../types/types';
import { AppRoute, SnackbarType, WebsiteSettingsOption } from '../types/enums';
import useErrorHandler from '../hooks/useErrorHandler';
import { DataContext, NotificationContext } from '../App';
import { DEFAULT_WEBSITE_FORM_STATE } from '../components/WebsiteForm/WebsiteForm';
import Panel from '../layout/Panel';

const EditWebsite = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const errorHandler = useErrorHandler();
  const notify = useContext(NotificationContext);
  const { refreshWebsites } = useContext(DataContext);
  const [details, setDetails] = useState<WebsiteSettings>();

  const onSave = useCallback(
    (form: WebsiteSettings) => {
      if (id) {
        updateWebsite(id, form).then((res) => {
          if (res.data) {
            notify('Website updated successfully', SnackbarType.SUCCESS);
            refreshWebsites();
            navigate(AppRoute.WEBSITES);
          }
        }, errorHandler);
      }
    },
    [id]
  );

  useEffect(() => {
    if (id) {
      fetchWebsiteDetails(id)
        .then(({ data }) => {
          setDetails({
            url: data.url,
            name: data.name,
            dashboardIds: (data.dashboards ?? []).map(({ id }) => id),
            iconPath: data.iconPath,
            httpStatusRule:
              data.settings?.httpStatusError?.comparator || WebsiteSettingsOption.IS_NOT_ONE_OF,
            httpStatusCode: data.settings?.httpStatusError?.value || '',
            keywordRule:
              data.settings?.keywordsError?.comparator || WebsiteSettingsOption.INCLUDES_KEYWORD,
            keywords: data.settings?.keywordsError?.value || '',
            loadTimeError:
              (data.settings?.loadSpeedError?.value as WebsiteSettingsOption) ||
              DEFAULT_WEBSITE_FORM_STATE.loadTimeError,
            sslExpireError:
              (data.settings?.sslStatusError?.value as WebsiteSettingsOption) ||
              DEFAULT_WEBSITE_FORM_STATE.sslExpireError,
            loadTimeWarning:
              (data.settings?.loadSpeedWarning?.value as WebsiteSettingsOption) ||
              DEFAULT_WEBSITE_FORM_STATE.loadTimeWarning,
            sslExpireWarning:
              (data.settings?.sslStatusWarning?.value as WebsiteSettingsOption) ||
              DEFAULT_WEBSITE_FORM_STATE.sslExpireWarning,
            errors: {},
            active: {
              httpStatusRule: !!data.settings?.httpStatusError?.active,
              keywordRule: !!data.settings?.keywordsError?.active,
              loadTimeError: !!data.settings?.loadSpeedError?.active,
              loadTimeWarning: !!data.settings?.loadSpeedWarning?.active,
              sslExpireError: !!data.settings?.sslStatusError?.active,
              sslExpireWarning: !!data.settings?.sslStatusWarning?.active
            }
          });
        })
        .catch(() => {
          navigate(AppRoute.WEBSITES);
        });
    } else {
      navigate(AppRoute.WEBSITES);
    }
  }, []);

  return (
    <Frame>
      <Panel>
        <h1>EDIT WEBSITE</h1>
        {details && <WebsiteForm onSave={onSave} initialState={details} />}
      </Panel>
    </Frame>
  );
};

export default EditWebsite;
